import { useMutation, useQuery } from '@tanstack/react-query';
import { type OnChangeFn, type SortingState, useTable } from '@tanstack/react-table';
import { FilterIcon, Funnel, RotateCw } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { DataUpload } from '@/common/data-upload';
import { ROLE } from '@/common/role';
import { BulkActionBar } from '@/components/bulk/BulkActionBar';
import { DataTable } from '@/components/data-table/data-table';
import { DeletedViewBanner } from '@/components/soft-delete/DeletedViewBanner';
import { RestoreConfirmDialog } from '@/components/soft-delete/RestoreConfirmDialog';
import { DELETED_STATUS, StatusFilterSelect } from '@/components/soft-delete/StatusFilterSelect';
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAssetReadOnly } from '@/context/AssetReadOnlyContext';
import { useTilesetLocationEdit } from '@/context/TilesetLocationEditContext';
import { useBulkSelection } from '@/hooks/use-bulk-selection';
import { usePermission } from '@/hooks/use-permission';
import { useRestoreItem } from '@/hooks/use-soft-delete';
import { extractRequestErrorMessage } from '@/lib/utils/axios';
import { reportBulkDelete, reportBulkRestore, runBulkDelete, runBulkRestore } from '@/lib/utils/bulk-delete';
import { invalidateAfterSoftDelete } from '@/lib/utils/soft-delete';
import { apiErrorMessage } from '@/queries/common';
import { deleteDataUpload } from '@/queries/data-uploads/delete-data-upload';
import { getTilesetLog } from '@/queries/data-uploads/get-tileset-log';
import type { DataUploadsListQueryFilterType } from '@/queries/data-uploads/list-data-uploads';
import { reprocessTileset } from '@/queries/data-uploads/reprocess-tileset';
import { queryClient } from '@/queries/query.client';
import { restoreItem, softDeleteTargets } from '@/queries/soft-delete/soft-delete';
import { getListSubAssetsOptions } from '../../../queries/sub-assets/list-sub-assets';
import { dataUploadColumns, dataUploadTableFeatures } from './columns';
import { DataUploadPreviewEditModal } from './DataUploadPreviewEditModal';

export type DataUploadListProps = {
	assetId?: string;
	dataUploads: DataUpload[];
	filters: DataUploadsListQueryFilterType;
	onFilterChange: (newFilters: DataUploadsListQueryFilterType) => void;
	doRefresh?: () => void;
	/** Server-side sort state, owned by the container so it can go into the list query. */
	sorting?: SortingState;
	onSortingChange?: OnChangeFn<SortingState>;
	/** "Show deleted" (GH #331), owned by the container so it can go into the list query */
	showDeleted?: boolean;
	onShowDeletedChange?: (showDeleted: boolean) => void;
};

const EMPTY_SORTING: SortingState = [];

export function DataUploadList({
	dataUploads,
	filters,
	onFilterChange,
	assetId,
	doRefresh,
	sorting = EMPTY_SORTING,
	onSortingChange,
	showDeleted = false,
	onShowDeletedChange,
}: DataUploadListProps) {
	const { t } = useTranslation();
	const onFileTypeFilterChange = (value: string) => {
		if (value === 'all') {
			value = '';
		}
		onFilterChange({ fileType: value });
	};
	const onAssetStructureFilterChange = (value: string) => {
		if (value === 'all') {
			value = '';
		}
		onFilterChange({ subAssetId: value });
	};

	const [previewModalState, setPreviewModalState] = useState({
		open: false,
		currentDataUpload: null as DataUpload | null,
	});
	// The tileset queued for a reprocess confirmation (null = dialog closed).
	const [reprocessTarget, setReprocessTarget] = useState<DataUpload | null>(null);
	// The tileset whose conversion log is being viewed (null = dialog closed).
	const [logTarget, setLogTarget] = useState<DataUpload | null>(null);

	// Multi-select + bulk delete
	const canDeleteUploads = usePermission(ROLE.DATA_UPLOADS_DELETE);
	const uploadIds = useMemo(() => dataUploads.map((dataUpload) => dataUpload.id as string), [dataUploads]);
	const selection = useBulkSelection(uploadIds);
	const [isBulkDeleting, setIsBulkDeleting] = useState(false);
	const fileLabel = {
		singular: t('common.entities.file', { count: 1 }),
		plural: t('common.entities.file', { count: 2 }),
	};
	const deleted = canDeleteUploads && showDeleted;
	const restoreUploads = async (ids: string[]) => {
		const result = await runBulkRestore(ids, (id) => restoreItem(softDeleteTargets.dataUpload(id)));
		reportBulkRestore(result, fileLabel);
		await invalidateAfterSoftDelete(queryClient, result.deleted);
		doRefresh?.();
	};
	const handleBulkDelete = async () => {
		setIsBulkDeleting(true);
		try {
			const result = await runBulkDelete(selection.selectedIds, deleteDataUpload);
			reportBulkDelete(result, fileLabel, (deletedIds) => void restoreUploads(deletedIds));
			selection.clear();
			await invalidateAfterSoftDelete(queryClient, result.deleted);
			doRefresh?.();
		} finally {
			setIsBulkDeleting(false);
		}
	};
	const handleBulkRestore = async () => {
		setIsBulkDeleting(true);
		try {
			await restoreUploads(selection.selectedIds);
			selection.clear();
		} finally {
			setIsBulkDeleting(false);
		}
	};
	const [uploadToRestore, setUploadToRestore] = useState<DataUpload | null>(null);
	const restoreUpload = useRestoreItem({
		onRestored: () => {
			setUploadToRestore(null);
			doRefresh?.();
		},
	});

	const logQuery = useQuery({
		queryKey: ['data-uploads', 'tileset-log', logTarget?.id],
		queryFn: () => getTilesetLog(logTarget?.id ?? ''),
		enabled: !!logTarget?.id,
	});
	const { start: startLocationEdit } = useTilesetLocationEdit();
	const { isReadOnly } = useAssetReadOnly();

	const reprocessApi = useMutation({
		mutationFn: reprocessTileset,
		onSuccess: async (updated) => {
			toast.success(t('files.tileset.reprocessStarted', { name: updated?.name ?? '' }));
			// Broad invalidation so the list badge and the main map's tile loader refresh.
			await queryClient.invalidateQueries({ queryKey: ['data-uploads'] });
			doRefresh?.();
		},
		onError: (error) => {
			console.error('Tileset reprocess error:', error);
			toast.error(t('files.tileset.reprocessFailed'), {
				description: apiErrorMessage(error),
			});
		},
	});

	const { data: subAssetsResponse } = useQuery(getListSubAssetsOptions(assetId || '', { limit: 300, offset: 0 }));
	const subAssets = subAssetsResponse?.subAssets || [];

	const table = useTable({
		features: dataUploadTableFeatures,
		columns: dataUploadColumns,
		data: dataUploads,
		getRowId: (row) => row.id as string,
		state: { sorting, columnVisibility: { select: canDeleteUploads } },
		onSortingChange,
		// The API sorts the whole result set; sorting client-side would only reorder the fetched page.
		manualSorting: true,
		// Skip auto-detecting the first direction from row values; always start ascending.
		sortDescFirst: false,
		meta: {
			isReadOnly,
			isReprocessPending: reprocessApi.isPending,
			onShowLog: setLogTarget,
			onReprocess: setReprocessTarget,
			onSetLocation: startLocationEdit,
			selection,
			onRestore: deleted ? setUploadToRestore : undefined,
		},
	});

	const onPreviewModalOpenChange = (open: boolean) => {
		if (!open) {
			setPreviewModalState({ open: false, currentDataUpload: null });
		}
	};

	return (
		<div className="space-y-4">
			<div className="flex gap-2 items-center bg-card text-card-foreground p-3 rounded-lg border shadow-sm overflow-x-auto">
				<Funnel className="size-4 text-muted-foreground shrink-0" />
				<div className="h-4 w-px bg-border mx-2 shrink-0" />
				<div className="flex items-center gap-2 min-w-0">
					<Select onValueChange={onFileTypeFilterChange} value={filters.fileType || 'all'}>
						<SelectTrigger className="bg-white text-sm h-8 w-36 shrink-0">
							<SelectValue placeholder={t('files.list.allExtensions')} />
						</SelectTrigger>
						<SelectContent position="popper">
							<SelectItem value="all">{t('files.list.allExtensions')}</SelectItem>
							<SelectItem value="image/jpg">.jpg</SelectItem>
							<SelectItem value="image/jpeg">.jpeg</SelectItem>
							<SelectItem value="obj">.obj</SelectItem>
							<SelectItem value="application/pdf">.pdf</SelectItem>
							<SelectItem value="image/gif">.gif</SelectItem>
							<SelectItem value="image/png">.png</SelectItem>
							<SelectItem value="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet">.xlsx</SelectItem>
							<SelectItem value="application/vnd.ms-excel">.xls</SelectItem>
							<SelectItem value="text/csv">.csv</SelectItem>
							<SelectItem value="text/plain">.txt</SelectItem>
							<SelectItem value="application/vnd.openxmlformats-officedocument.wordprocessingml.document">
								.docx
							</SelectItem>
						</SelectContent>
					</Select>
					<Select onValueChange={onAssetStructureFilterChange} value={filters.subAssetId || 'all'}>
						<SelectTrigger className="bg-white text-sm h-8 w-36 shrink-0">
							<SelectValue placeholder={t('files.list.allAssets')} />
						</SelectTrigger>
						<SelectContent position="popper" className="bg-muted">
							<SelectItem value="all">{t('files.list.allAssets')}</SelectItem>
							{subAssets.map((sa) => (
								<SelectItem key={sa.id} value={sa.id as string}>
									{sa.name}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
					{/* 3D conversion status, or "Deleted" for the deleted files (GH #331) */}
					<StatusFilterSelect
						value={showDeleted ? DELETED_STATUS : filters.tilesetStatus || 'all'}
						onValueChange={(value) => {
							const toDeleted = value === DELETED_STATUS;
							if (toDeleted !== showDeleted) {
								// The rows change completely: a selection of active files must not reach "Restore selected"
								selection.clear();
								onShowDeletedChange?.(toDeleted);
							}
							onFilterChange({ tilesetStatus: toDeleted || value === 'all' ? '' : value });
						}}
						options={[
							{ value: 'all', label: t('files.list.allStatuses') },
							{ value: 'queued', label: t('files.tileset.statusQueued') },
							{ value: 'processing', label: t('files.tileset.statusProcessing') },
							{ value: 'ready', label: t('files.tileset.ready') },
							{ value: 'error', label: t('files.tileset.statusError') },
						]}
						canShowDeleted={canDeleteUploads && !!onShowDeletedChange}
						ariaLabel={t('common.deleted.statusFilter')}
						className="w-36"
					/>
				</div>
			</div>
			{deleted && <DeletedViewBanner />}
			{canDeleteUploads && selection.count > 0 && (
				<BulkActionBar
					count={selection.count}
					total={dataUploads.length}
					label={fileLabel}
					mode={deleted ? 'restore' : 'delete'}
					isDeleting={isBulkDeleting}
					onClear={selection.clear}
					onDelete={deleted ? handleBulkRestore : handleBulkDelete}
				/>
			)}
			<DataTable
				table={table}
				tableClassName="table-fixed w-full"
				// A deleted file opens the same preview, read-only (GH #331)
				onRowClick={(row) => setPreviewModalState({ open: true, currentDataUpload: row.original })}
				getRowClassName={(row) => selection.isSelected(row.original.id as string) && 'bg-primary/5'}
				emptyState={
					<div className="text-center py-12 bg-card rounded-lg border border-dashed text-card-foreground">
						<FilterIcon className="h-8 w-8 mx-auto mb-2 opacity-50" />
						<p>{deleted ? t('common.deleted.empty') : t('files.list.noResults')}</p>
					</div>
				}
			/>
			{uploadToRestore && (
				<RestoreConfirmDialog
					open
					onOpenChange={(open) => !open && setUploadToRestore(null)}
					target={softDeleteTargets.dataUpload(uploadToRestore.id as string)}
					name={uploadToRestore.name || t('common.entities.file', { count: 1 })}
					isRestoring={restoreUpload.isPending}
					onConfirm={() =>
						restoreUpload.mutate({
							target: softDeleteTargets.dataUpload(uploadToRestore.id as string),
							name: uploadToRestore.name || t('common.entities.file', { count: 1 }),
						})
					}
				/>
			)}
			<DataUploadPreviewEditModal
				open={previewModalState.open}
				onOpenChange={onPreviewModalOpenChange}
				dataUpload={previewModalState.currentDataUpload}
				subAssets={subAssets}
				assetId={assetId}
				doRefresh={doRefresh}
				deleted={deleted}
			/>
			<Dialog open={!!logTarget} onOpenChange={(open) => !open && setLogTarget(null)}>
				<DialogContent className="sm:max-w-2xl">
					<DialogHeader>
						<DialogTitle>{t('files.list.conversionLog')}</DialogTitle>
						<DialogDescription>
							{t('files.list.conversionLogDescription', { name: logTarget?.name ?? '' })}
						</DialogDescription>
					</DialogHeader>
					{logQuery.isLoading ? (
						<p className="text-sm text-muted-foreground">{t('files.list.loadingLog')}</p>
					) : logQuery.isError ? (
						<p className="text-sm text-destructive">
							{extractRequestErrorMessage(logQuery.error)
								? apiErrorMessage(logQuery.error)
								: t('files.list.logLoadFailed')}
						</p>
					) : (
						<pre className="max-h-[60vh] overflow-auto rounded-md bg-muted p-3 text-xs whitespace-pre-wrap break-words">
							{logQuery.data?.log || t('files.list.noLogYet')}
						</pre>
					)}
				</DialogContent>
			</Dialog>
			<AlertDialog open={!!reprocessTarget} onOpenChange={(open) => !open && setReprocessTarget(null)}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>{t('files.tileset.reprocessTitle')}</AlertDialogTitle>
						<AlertDialogDescription>
							{t('files.tileset.reprocessBody', { name: reprocessTarget?.name ?? '' })}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>{t('common.actions.cancel')}</AlertDialogCancel>
						<AlertDialogAction
							onClick={() => {
								if (reprocessTarget?.id) reprocessApi.mutate(reprocessTarget.id);
								setReprocessTarget(null);
							}}
						>
							<RotateCw className="size-4" />
							{t('files.tileset.reprocessAction')}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	);
}
